import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import type { ColumnDef, SortingState, Updater } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type {
  ExactJournalEntryFilter,
  ExactJournalEntrySort,
  SortDirection,
} from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { formatDate, formatDateTime } from '@/lib/format';
import { useListHref } from '@/hooks/useListLocation';
import { sortingFromUrl, sortingToUrl, useUrlState } from '@/hooks/useUrlState';
import {
  EXACT_COLUMN_SORT,
  EXACT_DEFAULT_SORT,
  EXACT_STATUS_OPTIONS,
  exactUrlSchema,
  type ExactEntryStatusValue,
} from './exactUrlState';

const ExactJournalEntriesDoc = graphql(/* GraphQL */ `
  query ExactJournalEntries(
    $filter: ExactJournalEntryFilter
    $page: Int
    $pageSize: Int
    $sort: ExactJournalEntrySort
  ) {
    exactJournalEntries(filter: $filter, page: $page, pageSize: $pageSize, sort: $sort) {
      nodes {
        id
        recognitionDate
        status
        journalCode
        description
        financialYear
        financialPeriod
        exactEntryId
        entryNumber
        error
        createdAt
        updatedAt
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const ExportExactDayDoc = graphql(/* GraphQL */ `
  mutation ExportExactDay($recognitionDate: String!) {
    exportExactDay(recognitionDate: $recognitionDate) {
      id
      status
      error
    }
  }
`);

type ExactRow = {
  id: string;
  recognitionDate: string;
  status: string;
  journalCode: string;
  description: string;
  financialYear: number;
  financialPeriod: number;
  exactEntryId?: string | null;
  entryNumber?: number | null;
  error?: string | null;
  createdAt: string;
};

/**
 * Admin grid of Exact Online journal entries posted (or attempted) at cutoff.
 */
export function ExactEntriesListPage() {
  const { state: url, patch, setFilters, reset } = useUrlState(exactUrlSchema);
  const listHref = useListHref();
  const sorting = useMemo(() => sortingFromUrl(url), [url]);

  const filter = useMemo((): ExactJournalEntryFilter => {
    const f: ExactJournalEntryFilter = {};
    if (url.status) f.status = url.status;
    if (url.from || url.to) {
      f.recognitionDate = {
        from: url.from || undefined,
        to: url.to || undefined,
      };
    }
    return f;
  }, [url.status, url.from, url.to]);

  const sort = useMemo(
    (): ExactJournalEntrySort => ({
      field: EXACT_COLUMN_SORT[url.sort],
      direction: (url.dir === 'asc' ? 'ASC' : 'DESC') as SortDirection,
    }),
    [url.sort, url.dir],
  );

  const { data, previousData, loading, refetch } = useQuery(ExactJournalEntriesDoc, {
    variables: { filter, page: url.page, pageSize: url.size, sort },
  });
  const gridData = data ?? previousData;
  const rows = (gridData?.exactJournalEntries.nodes ?? []) as ExactRow[];
  const pageInfo = gridData?.exactJournalEntries.pageInfo;

  const [exportDay, { loading: retrying }] = useMutation(ExportExactDayDoc);

  const handleRetry = useCallback(
    async (recognitionDate: string) => {
      await exportDay({ variables: { recognitionDate } });
      await refetch();
    },
    [exportDay, refetch],
  );

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setFilters(sortingToUrl(next, EXACT_DEFAULT_SORT));
  };

  const columns = useMemo<ColumnDef<ExactRow>[]>(
    () => [
      {
        id: 'recognitionDate',
        accessorKey: 'recognitionDate',
        header: 'Recognition day',
        cell: ({ row }) => (
          <Link
            id={`admin.exact.entries.open.${row.original.recognitionDate}`}
            to={`/exact/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            {formatDate(row.original.recognitionDate)}
          </Link>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row, getValue }) => {
          const value = String(getValue());
          return (
            <Badge
              id={`admin.exact.entries.status.${row.original.recognitionDate}`}
              tone={value === 'succeeded' ? 'success' : 'error'}
            >
              {value}
            </Badge>
          );
        },
      },
      {
        id: 'journalCode',
        accessorKey: 'journalCode',
        header: 'Journal',
        enableSorting: false,
      },
      {
        id: 'entryNumber',
        accessorKey: 'entryNumber',
        header: 'Entry #',
        enableSorting: false,
        cell: ({ row, getValue }) => (
          <span id={`admin.exact.entries.entryNumber.${row.original.recognitionDate}`}>
            {(getValue() as number | null | undefined) ?? '—'}
          </span>
        ),
      },
      {
        id: 'period',
        header: 'Period',
        enableSorting: false,
        cell: ({ row }) =>
          `${row.original.financialYear}-${String(row.original.financialPeriod).padStart(2, '0')}`,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ getValue }) => formatDateTime(String(getValue())),
      },
      {
        id: 'error',
        accessorKey: 'error',
        header: 'Error',
        enableSorting: false,
        cell: ({ getValue }) => {
          const msg = getValue() as string | null | undefined;
          if (!msg) return '—';
          return (
            <span
              className="line-clamp-2 max-w-xs text-xs text-red-600 dark:text-red-400"
              title={msg}
            >
              {msg}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status === 'failed' ? (
            <Button
              size="sm"
              variant="outline"
              loading={retrying}
              onClick={() => void handleRetry(row.original.recognitionDate)}
            >
              Retry
            </Button>
          ) : null,
      },
    ],
    [handleRetry, retrying, listHref],
  );

  return (
    <div id="admin.exact.entries" className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Exact</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Daily tax-report bookings posted to Exact Online at cutoff.
        </p>
      </div>

      <FilterBar
        advanced={
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label
                htmlFor="exact-entries-status"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Status
              </label>
              <Select
                id="exact-entries-status"
                value={url.status}
                onChange={(e) => setFilters({ status: e.target.value as ExactEntryStatusValue | '' })}
                options={EXACT_STATUS_OPTIONS}
              />
            </div>
            <div>
              <label
                htmlFor="exact-entries-from"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                From
              </label>
              <Input
                id="exact-entries-from"
                type="date"
                value={url.from}
                onChange={(e) => setFilters({ from: e.target.value })}
              />
            </div>
            <div>
              <label
                htmlFor="exact-entries-to"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                To
              </label>
              <Input
                id="exact-entries-to"
                type="date"
                value={url.to}
                onChange={(e) => setFilters({ to: e.target.value })}
              />
            </div>
          </div>
        }
        onClearAll={reset}
        defaultOpen
      />

      <DataGrid<ExactRow>
        data={rows}
        columns={columns}
        loading={loading && !gridData}
        rowId={(row) => row.id}
        rowDomId={(row) => `admin.exact.entries.row.${row.recognitionDate}`}
        sorting={{ state: sorting, onChange: handleSortingChange }}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
